import { LinearProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { useHealthRecord } from "../../../hooks/useHealthRecord";
import { useI18n } from "../../../hooks/useI18n";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useKairosApi } from "../../../hooks/useKairosApi";
import { encodeTravelDiagnosticData, TravelSnapshot } from "../travel/travelTypes";

export function IframeDiagnostic({ travel }: { travel?: TravelSnapshot }) {
  const { id } = useParams() as { id: string };
  const { healthRecord } = useHealthRecord(id);
  const mentorUrl = import.meta.env.VITE_MENTOR_URL || "";
  const { locale } = useI18n();
  const [evcToken, setEvcToken] = useState<string | undefined>(undefined);
  const [iframeHeight, setIframeHeight] = useState<number>(500);
  const [isEvcLoading, setIsEvcLoading] = useState<boolean>(true);
  const [completedIframeSrc, setCompletedIframeSrc] = useState<string>();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const kairosApi = useKairosApi();

  const mentorLocale = useMemo(() => {
    switch (locale) {
      case "fr":
        return "fr";
      case "gr":
        return "gr";
      case "en-gb":
        return "en";
      default:
        return "en";
    }
  }, [locale]);

  const iframeUrl = useMemo(() => {
    if (!mentorUrl) return "";

    const url = new URL(mentorUrl);
    url.pathname = url.hostname.includes("sadv-iframe") ? "/" : "/iframe";
    url.search = "";
    url.hash = "";
    return url.toString();
  }, [mentorUrl]);

  const iframeSrc = useMemo(() => {
    if (!iframeUrl || !evcToken) return "";

    const url = new URL(iframeUrl);
    url.searchParams.set("frozen", "true");
    url.searchParams.set("lang", mentorLocale);
    url.searchParams.set("prevent-iframe-scroll", "true");
    url.searchParams.set("evc", evcToken);
    if (travel) {
      url.searchParams.set("travel-data", encodeTravelDiagnosticData(travel));
    } else {
      url.searchParams.set("step", "recommandations");
    }
    return url.toString();
  }, [evcToken, iframeUrl, mentorLocale, travel]);

  useLayoutEffect(() => {
    if (!iframeUrl || !iframeSrc) return;
    const handleMessage = (event: MessageEvent) => {
      const expectedOrigin = new URL(iframeUrl).origin;
      if (event.origin !== expectedOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      if (typeof event.data !== "object" || event.data === null) return;

      switch (event.data.type) {
        case "documentOpen":
          if (event.data.data?.url) {
            window.open(event.data.data.url, "_blank");
          }
          break;
        case "heightChange":
          if (typeof event.data.data?.height === "number") {
            setIframeHeight(Math.max(event.data.data.height + 20, 500));
          }
          break;
        case "diagnosticLoadingCompleted":
          setCompletedIframeSrc(iframeSrc);
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [iframeSrc, iframeUrl]);

  useEffect(() => {
    let cancelled = false;
    const fetchEvcToken = async () => {
      try {
        setIsEvcLoading(true);
        const { jwt } = await kairosApi!.healthRecordApi.getEvcToken(id);
        if (!cancelled) setEvcToken(jwt);
      } catch (error) {
        console.error("Error fetching EVC token:", error);
      } finally {
        if (!cancelled) setIsEvcLoading(false);
      }
    };

    fetchEvcToken();
    return () => {
      cancelled = true;
    };
  }, [id, kairosApi]);

  const isLoading = isEvcLoading || !iframeSrc || completedIframeSrc !== iframeSrc;

  if (!healthRecord) {
    return <LinearProgress variant="query" />;
  }

  return (
    <div style={{ position: "relative", minHeight: 200 }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 10,
          }}
        >
          <LinearProgress variant="query" />
        </div>
      )}
      <iframe
        key={iframeSrc}
        ref={iframeRef}
        src={iframeSrc || undefined}
        width="100%"
        height={iframeHeight}
        title="Mentor par MesVaccins.net"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
        style={{ border: 0, opacity: isLoading ? 0 : 1, transition: "opacity 0.3s" }}
      />
    </div>
  );
}
